/**
 * @fileoverview AR-932 — "All students" lives inside the students select as its first option, so the
 * teacher picks the whole class the same way they pick one student. The option is a sentinel value:
 * it never reaches the staged item, which always carries the real student ids.
 */

import { coversEveryone } from './stagedItems'

export const ALL_STUDENTS_OPTION = '__all_students__'

export interface StudentOption {
  readonly value: string
  readonly label: string
}

/** Prepends "All students" when there is more than one student to choose from. */
export function withAllStudentsOption(
  studentOptions: readonly StudentOption[],
  allStudentsLabel: string,
): StudentOption[] {
  if (studentOptions.length < 2) return [...studentOptions]
  return [{ value: ALL_STUDENTS_OPTION, label: allStudentsLabel }, ...studentOptions]
}

/** What the select shows: a single "All students" chip when every student is covered, else the ids. */
export function selectValueOf(studentIds: readonly string[], everyone: readonly string[]): string[] {
  return coversEveryone(studentIds, everyone) ? [ALL_STUDENTS_OPTION] : [...studentIds]
}

/**
 * Turns the select's next value into student ids.
 * - picking "All students" → everyone
 * - removing the "All students" chip → nobody
 * - picking a student while "All students" is on → just the students picked (narrowing down)
 */
export function resolveStudentSelection(
  previousIds: readonly string[],
  nextValue: readonly string[],
  everyone: readonly string[],
): string[] {
  const hadAll = coversEveryone(previousIds, everyone)
  const hasAll = nextValue.includes(ALL_STUDENTS_OPTION)
  const picked = nextValue.filter(value => value !== ALL_STUDENTS_OPTION)
  if (hasAll && !hadAll) return [...everyone]
  if (!hasAll && hadAll) return []
  return picked
}
